import { WEEK_DAYS, TFactoryShiftDetails, TShiftWeekDays, TLineShiftDetails, TLineShiftSlot } from '@monorepo/types';
import moment from 'moment';
import { DateUtils } from '@monorepo/utils';
import { ModifiersZTableModel } from '../../table.modifier';

const WEEK_DAYS_DISPLAY_NAME_MAP: Record<(typeof WEEK_DAYS)[number], string> = {
  sunday: 'shiftManagement.Sun',
  monday: 'shiftManagement.Mon',
  tuesday: 'shiftManagement.Tue',
  wednesday: 'shiftManagement.Wed',
  thursday: 'shiftManagement.Thu',
  friday: 'shiftManagement.Fri',
  saturday: 'shiftManagement.Sat',
};

export const FACTORY_SHIFTS_TABLE_MODEL = {
  createModel: (shifts: TFactoryShiftDetails[], factoryTimeZone?: string) =>
    ModifiersZTableModel.createTableModel(
      shifts,
      {
        headerConfig: [
          { type: 'TEXT_CELL', text: 'shiftManagement.ShiftName', maxWidthInPx: 150 },
          { type: 'TEXT_CELL', text: 'shiftManagement.ShiftTimings', maxWidthInPx: 150 },
          { type: 'TEXT_CELL', text: 'shiftManagement.BreakTime', maxWidthInPx: 150 },
          { type: 'TEXT_CELL', text: 'shiftManagement.WorkingDays', maxWidthInPx: 150 },
          { type: 'ACTION_CELL', actionType: 'ASSIGN_SHIFT_TO_LOCATIONS', widthInPx: 200 },
        ],
      },
      (shift) => ({
        type: 'ROW',
        rowItem: shift,
        cells: [
          { type: 'TEXT_CELL', text: shift.shiftName },
          {
            type: 'TEXT_CELL',
            text: `${getFormattedTime(shift.shiftTime.start, factoryTimeZone)} - ${getFormattedTime(
              shift.shiftTime.end,
              factoryTimeZone,
            )}`,
          },
          {
            type: 'TEXT_LIST_CELL',
            text: shift.breaks.map(
              (b) => `${getFormattedTime(b.start, factoryTimeZone)} - ${getFormattedTime(b.end, factoryTimeZone)}`,
            ),
          },
          {
            type: 'TEXT_LIST_CELL',
            text: getWorkingDaysDisplayName(shift.workingDays),
            orientation: 'HORIZONTAL',
            flexWrap: 'wrap',
          },
          { type: 'ACTION_CELL', actionType: 'ASSIGN_SHIFT_TO_LOCATIONS', actionName: 'shiftManagement.AssignToLines' },
        ],
      }),
    ),
};

const EMPTY_CELL_TEXT = '- -';

export const LINE_SHIFTS_TABLE_MODEL = {
  createModel: (lines: TLineShiftDetails[], factoryTimeZone?: string) =>
    ModifiersZTableModel.createTableModel(
      lines,
      {
        headerConfig: [
          { type: 'TEXT_CELL', text: 'shiftManagement.Line', maxWidthInPx: 150 },
          { type: 'TEXT_CELL', text: 'shiftManagement.CurrentShiftName', maxWidthInPx: 150 },
          { type: 'TEXT_CELL', text: 'shiftManagement.ShiftTimings', maxWidthInPx: 150 },
          { type: 'TEXT_CELL', text: 'shiftManagement.BreakTime', maxWidthInPx: 150 },
          { type: 'TEXT_CELL', text: 'shiftManagement.WorkingDays', maxWidthInPx: 150 },
          { type: 'TEXT_CELL', text: 'shiftManagement.StartDate', maxWidthInPx: 150 },
          { type: 'TEXT_CELL', text: 'shiftManagement.UpcomingShiftName', maxWidthInPx: 150 },
        ],
      },
      (lineShift) => {
        const currentShift = lineShift.currentShiftOpt;
        const breaks = currentShift?.breaks ?? [];
        const workingDays = currentShift?.workingDays ? getWorkingDaysDisplayName(currentShift.workingDays) : [];
        return {
          type: 'ROW',
          rowItem: lineShift,
          cells: [
            {
              type: 'TEXT_CELL',
              text: lineShift.departmentOpt
                ? `Line ${lineShift.line} (${lineShift.departmentOpt})`
                : `Line ${lineShift.line}`,
            },
            { type: 'TEXT_CELL', text: currentShift ? getShiftDisplayName(currentShift) : EMPTY_CELL_TEXT },
            {
              type: 'TEXT_CELL',
              text: currentShift?.shiftTime
                ? `${getFormattedTime(currentShift.shiftTime.start, factoryTimeZone)} - ${getFormattedTime(
                    currentShift.shiftTime.end,
                    factoryTimeZone,
                  )}`
                : EMPTY_CELL_TEXT,
            },
            {
              type: 'TEXT_LIST_CELL',
              text: breaks.length
                ? breaks.map(
                    (b) =>
                      `${getFormattedTime(b.start, factoryTimeZone)} - ${getFormattedTime(b.end, factoryTimeZone)}`,
                  )
                : [EMPTY_CELL_TEXT],
            },
            {
              type: 'TEXT_LIST_CELL',
              text: workingDays.length ? workingDays : [EMPTY_CELL_TEXT],
              orientation: 'HORIZONTAL',
              flexWrap: 'wrap',
            },
            { type: 'TEXT_CELL', text: currentShift?.startDate || EMPTY_CELL_TEXT },
            {
              type: 'TEXT_CELL',
              text: lineShift.upcomingShiftOpt ? getShiftDisplayName(lineShift.upcomingShiftOpt) : EMPTY_CELL_TEXT,
            },
          ],
        };
      },
    ),
};

function getShiftDisplayName(shift: TLineShiftSlot) {
  return shift.shiftCategory ? `${shift.shiftName} (${shift.shiftCategory})` : shift.shiftName;
}

function getWorkingDaysDisplayName(workingDays: TShiftWeekDays) {
  const days = WEEK_DAYS.filter((day) => workingDays[day]);

  return days.flatMap((day, index) =>
    index < days.length - 1 ? [WEEK_DAYS_DISPLAY_NAME_MAP[day], ', '] : [WEEK_DAYS_DISPLAY_NAME_MAP[day]],
  );
}

function getFormattedTime(epoch: number, factoryTimeZone?: string) {
  return moment(DateUtils.getTimeFromEpoch(epoch, factoryTimeZone), 'HH:mm').format('hh:mm A');
}
